import React, { useState } from "react";

import { Opportunity, PipelineStage } from "../types/crm";

export interface MoveStagePayload {
  meeting_date?: string;
  result?: "ganho" | "perdido";
  fup_date?: string;
  next_action: string;
  replace_fup: boolean;
}

interface MoveStageModalProps {
  opportunity: Opportunity;
  targetStage: PipelineStage;
  onCancel: () => void;
  onConfirm: (payload: MoveStagePayload) => Promise<void>;
}

/**
 * Modal de movimentação de card no Kanban (Frente 3). Sugere a data do FUP e a
 * próxima ação conforme a etapa destino — ambas pré-preenchidas e editáveis.
 * As sugestões vêm do backend (config por etapa).
 */
export function MoveStageModal({
  opportunity,
  targetStage,
  onCancel,
  onConfirm,
}: MoveStageModalProps) {
  const isImplementation = targetStage.is_implementation;
  const isMeeting = targetStage.requires_meeting_date;
  // "Implantado" é fechado, mas tem fluxo próprio (sem resultado/FUP).
  const isClosed = targetStage.is_closed && !isImplementation;

  const [meetingDate, setMeetingDate] = useState("");
  const [fupDate, setFupDate] = useState(targetStage.suggested_fup_date ?? "");
  const [result, setResult] = useState<"ganho" | "perdido" | "">("");
  const [nextAction, setNextAction] = useState(
    isClosed || isImplementation ? "" : targetStage.suggested_next_action
  );
  const [actionTouched, setActionTouched] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Card já tinha um FUP pendente e a etapa destino cria um novo → perguntar
  // se quer substituir.
  const showReplace = opportunity.has_pending_followup && !isClosed;
  const [replaceFup, setReplaceFup] = useState(true);

  const handleResultChange = (value: "ganho" | "perdido") => {
    setResult(value);
    // Segue a sugestão do resultado enquanto o usuário não editou o texto.
    if (!actionTouched) {
      setNextAction(
        value === "ganho"
          ? targetStage.suggested_next_action_won
          : targetStage.suggested_next_action_lost
      );
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setFieldError(null);

    if (isMeeting && !meetingDate) {
      setFieldError("Informe a data da reunião.");
      return;
    }
    if (isClosed && !result) {
      setFieldError("Selecione o resultado.");
      return;
    }
    if (!isMeeting && !isClosed && !isImplementation && !fupDate) {
      setFieldError("Informe a data do follow-up.");
      return;
    }

    const payload: MoveStagePayload = {
      next_action: nextAction,
      replace_fup: showReplace ? replaceFup : true,
    };
    if (isMeeting) payload.meeting_date = meetingDate;
    else if (isClosed) payload.result = result as "ganho" | "perdido";
    else if (!isImplementation) payload.fup_date = fupDate;

    setIsSubmitting(true);
    try {
      await onConfirm(payload);
    } catch {
      setFormError("Não foi possível mover a oportunidade. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 py-16">
      <div className="w-full max-w-sm rounded-[10px] border border-border-subtle bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-800">
          Mover para "{targetStage.name}"
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          {opportunity.company_detail.name}
        </p>

        <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
          {isMeeting && (
            <div>
              <label htmlFor="ms_meeting" className="block text-sm font-medium text-slate-700">
                Data da reunião
              </label>
              <input
                id="ms_meeting"
                type="date"
                value={meetingDate}
                onChange={(event) => setMeetingDate(event.target.value)}
                className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          )}

          {isClosed && (
            <div>
              <label className="block text-sm font-medium text-slate-700">Resultado</label>
              <div className="mt-1 space-y-1">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="radio"
                    name="ms_result"
                    checked={result === "ganho"}
                    onChange={() => handleResultChange("ganho")}
                  />
                  Ganho
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="radio"
                    name="ms_result"
                    checked={result === "perdido"}
                    onChange={() => handleResultChange("perdido")}
                  />
                  Perdido
                </label>
              </div>
            </div>
          )}

          {isImplementation && (
            <p className="rounded-[10px] bg-accent-soft px-3 py-2 text-sm text-accent-dark">
              Marca este negócio ganho como implantado. Ele sai da lista de
              pendentes de implantação.
            </p>
          )}

          {!isMeeting && !isClosed && !isImplementation && (
            <div>
              <label htmlFor="ms_fup" className="block text-sm font-medium text-slate-700">
                Data do follow-up
              </label>
              <input
                id="ms_fup"
                type="date"
                value={fupDate}
                onChange={(event) => setFupDate(event.target.value)}
                className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <p className="mt-1 text-xs text-slate-400">Sugerida automaticamente. Você pode ajustar.</p>
            </div>
          )}

          <div>
            <label htmlFor="ms_action" className="block text-sm font-medium text-slate-700">
              {isImplementation ? "Observação (opcional)" : "Próxima ação"}
            </label>
            <textarea
              id="ms_action"
              rows={2}
              value={nextAction}
              onChange={(event) => {
                setActionTouched(true);
                setNextAction(event.target.value);
              }}
              className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {showReplace && (
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={replaceFup}
                onChange={(event) => setReplaceFup(event.target.checked)}
                className="mt-0.5"
              />
              Já existe um FUP pendente. Substituir pela nova data?
            </label>
          )}

          {fieldError && <p className="text-sm text-red-600">{fieldError}</p>}
          {formError && <p className="text-sm text-red-600">{formError}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-[10px] px-4 py-2 text-sm font-medium text-slate-600 hover:bg-surface"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark disabled:opacity-60"
            >
              {isSubmitting ? "Salvando..." : "Confirmar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
